import type {
  CreateProductScheduleRequest,
  ProductSchedule,
  UpdateProductScheduleRequest,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, toZonedInput } from '@nucleus-commerce/core';

import { emptyRules, fromRules, toRulesInput, type RuleValueKind } from '@/features/product-rules';

import type {
  PoolProductForm,
  ScheduleFormInput,
  ScheduleFormOutput,
} from '../schemas/schedule-form-schema';

type KindOf = (field: string) => RuleValueKind;

export interface NewScheduleDefaults {
  /** Zone of the first store the form starts with, else the browser's. */
  timeZone: string;
  /** Products handed over (the products index's "Create rotation…"), unpinned. */
  productIds?: readonly string[];
  /** Title per handed-over id when the caller knows it. */
  titles?: ReadonlyMap<string, string>;
  now?: Date;
}

const HOUR = 60 * 60 * 1000;

/** The next full hour, so a new schedule never starts in the past. */
function nextFullHour(now: Date): string {
  return new Date(Math.ceil((now.getTime() + 1) / HOUR) * HOUR).toISOString();
}

export function emptyScheduleForm({
  timeZone,
  productIds = [],
  titles,
  now = new Date(),
}: NewScheduleDefaults): ScheduleFormInput {
  return {
    name: '',
    stores: [],
    liveCount: '12',
    intervalDays: '7',
    runAt: '09:00',
    timeZone,
    startsAt: toZonedInput(nextFullHour(now), timeZone),
    endsAt: '',
    maxCycles: '',
    loop: true,
    onEnd: 'unpublish',
    notify: 'problems',
    rulesEnabled: false,
    rules: emptyRules(false),
    products: [...new Set(productIds)].map((id): PoolProductForm => ({
      id,
      title: titles?.get(id) ?? null,
      pinned: false,
    })),
  };
}

export function fromSchedule(schedule: ProductSchedule, kindOf: KindOf): ScheduleFormInput {
  return {
    name: schedule.name,
    stores: schedule.stores.map((store) => ({
      storeId: store.storeId,
      redirectCollectionId: store.redirectCollection?.id ?? null,
      redirectCollectionTitle: store.redirectCollection?.title ?? null,
    })),
    liveCount: String(schedule.liveCount),
    intervalDays: String(schedule.intervalDays),
    runAt: schedule.runAt,
    timeZone: schedule.timeZone,
    startsAt: toZonedInput(schedule.startsAt, schedule.timeZone),
    endsAt: toZonedInput(schedule.endsAt, schedule.timeZone),
    maxCycles: schedule.maxCycles === null ? '' : String(schedule.maxCycles),
    loop: schedule.loop,
    onEnd: schedule.onEnd,
    notify: schedule.notify,
    rulesEnabled: schedule.rules !== null,
    rules: schedule.rules ? fromRules(schedule.rules, kindOf) : emptyRules(false),
    products: [],
  };
}

/** A wall-clock value the schema accepted always resolves; the fallback only satisfies types. */
const instant = (value: string, timeZone: string): string =>
  fromZonedInput(value, timeZone) ?? new Date(value).toISOString();

/** Every schedule field as the API spells it; `stores` and `rules` included. */
function scheduleBody(values: ScheduleFormOutput, kindOf: KindOf) {
  return {
    name: values.name,
    stores: values.stores.map((store) => ({
      storeId: store.storeId,
      redirectCollectionId: store.redirectCollectionId,
    })),
    liveCount: Number(values.liveCount),
    intervalDays: Number(values.intervalDays),
    runAt: values.runAt,
    timeZone: values.timeZone,
    startsAt: instant(values.startsAt, values.timeZone),
    endsAt: values.endsAt === '' ? null : instant(values.endsAt, values.timeZone),
    maxCycles: values.maxCycles === '' ? null : Number(values.maxCycles),
    loop: values.loop,
    onEnd: values.onEnd,
    notify: values.notify,
    rules: values.rulesEnabled ? toRulesInput(values.rules, kindOf) : null,
  };
}

export interface CreateScheduleBody {
  body: CreateProductScheduleRequest;
  /** Added after the create with `pinned: true`; the create call takes no pin flag. */
  pinnedIds: string[];
}

export function toCreateSchedule(values: ScheduleFormOutput, kindOf: KindOf): CreateScheduleBody {
  return {
    body: {
      ...scheduleBody(values, kindOf),
      productIds: values.products.filter((p) => !p.pinned).map((p) => p.id),
    },
    pinnedIds: values.products.filter((p) => p.pinned).map((p) => p.id),
  };
}

/**
 * Only the fields that changed: an unchanged `stores` list would still be diffed by the server
 * and an unchanged cadence on an active schedule must not move its next run.
 */
export function toUpdateSchedule(
  values: ScheduleFormOutput,
  initial: ScheduleFormOutput,
  kindOf: KindOf,
): UpdateProductScheduleRequest {
  const next = scheduleBody(values, kindOf);
  const before = scheduleBody(initial, kindOf);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(next) as Array<keyof typeof next>) {
    if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) out[key] = next[key];
  }
  return out;
}

/** Backend field paths (without `body.`) on the form: `productIds` is the pool section. */
export function mapScheduleField(field: string): string {
  return field === 'productIds' ? 'products' : field;
}
